import { MAX_PAGE_SIZE } from "@miplatform/constants";
import { API_VERSION, TASK_SERVICE, X_API_VERSION } from "../../../constants";
import { apiOptions, apiRequest } from "../../../utils";

const API_ORDER = `${TASK_SERVICE}/orders`;

export const order = {
  /**
   * Gets the API options for creating a new order (without executing the request).
   *
   * @async
   * @function getCreateOptions
   * @param {Object} params - The parameters object
   * @param {Object} params.data - The order data to create
   * @returns {Promise<any>} The API options object for the create request
   */
  getCreateOptions: async ({ data }) =>
    apiOptions({
      url: API_ORDER,
      headers: { [X_API_VERSION]: API_VERSION },
      method: "post",
      data,
    }),

  /**
   * Creates a new order.
   *
   * @async
   * @function create
   * @param {Object} params - The parameters object
   * @param {Object} params.data - The order data to create
   * @param {Object} [params.toastMessageConfig] - Toast message configuration for UI feedback
   * @returns {Promise<any>} The created order response
   * @throws {Error} When the API request fails
   *
   * @example
   * const newOrder = await order.create({
   *   data: {
   *     customerId: "123",
   *     items: [...],
   *     deliveryDate: "2024-12-01"
   *   },
   *   toastMessageConfig: { showSuccess: true }
   * });
   */
  create: async ({ data, toastMessageConfig }) => {
    const options = await apiOptions({
      url: API_ORDER,
      headers: { [X_API_VERSION]: API_VERSION },
      method: "post",
      data,
    });
    return apiRequest(options, toastMessageConfig);
  },

  /**
   * Updates an existing order.
   *
   * @async
   * @function update
   * @param {Object} params - The parameters object
   * @param {string} params.orderId - The ID of the order to update
   * @param {Object} params.data - The updated order data
   * @param {Object} [params.toastMessageConfig] - Toast message configuration for UI feedback
   * @returns {Promise<any>} The updated order response
   * @throws {Error} When the API request fails
   *
   * @example
   * const updatedOrder = await order.update({
   *   orderId: "order-123",
   *   data: { status: "confirmed" },
   *   toastMessageConfig: { showSuccess: true }
   * });
   */
  update: async ({ orderId, data, toastMessageConfig }) => {
    const options = await apiOptions({
      url: `${API_ORDER}/${orderId}`,
      headers: { [X_API_VERSION]: API_VERSION },
      method: "patch",
      data,
    });
    return apiRequest(options, toastMessageConfig);
  },

  /**
   * Retrieves all orders with optional filtering, sorting, and pagination.
   *
   * @async
   * @function getAll
   * @param {Object} [params={}] - The query parameters object
   * @param {Object} [params.filters] - OData-style filters for the query
   * @param {Object} [params.sort] - Sorting configuration
   * @param {Object} [params.params] - Additional query parameters
   * @param {AbortSignal} [params.signal] - AbortController signal for request cancellation
   * @param {number} [params.top] - Maximum number of records to return (OData $top)
   * @param {number} [params.skip] - Number of records to skip (OData $skip)
   * @param {Object} [params.toastMessageConfig] - Toast message configuration for UI feedback
   * @returns {Promise<any>} The orders list response with metadata
   * @throws {Error} When the API request fails
   *
   * @example
   * const orders = await order.getAll({
   *   filters: { status: "active" },
   *   sort: { field: "createdDate", direction: "desc" },
   *   top: 50,
   *   skip: 0
   * });
   */
  getAll: async ({
    filters,
    sort,
    params,
    headers = {},
    signal,
    top,
    skip,
    toastMessageConfig,
  } = {}) => {
    const options = await apiOptions({
      url: API_ORDER,
      method: "get",
      headers: { ...headers, [X_API_VERSION]: API_VERSION },
      signal,
      params,
      filters,
      sort,
      top,
      skip,
    });
    return apiRequest(options, toastMessageConfig);
  },

  getAssignments: async ({
    assignedDate = "",
    top = MAX_PAGE_SIZE,
    headers = {},
    toastMessageConfig = {},
  } = {}) => {
    const options = await apiOptions({
      url: `${API_ORDER}/assignments`,
      method: "get",
      headers: { ...headers, [X_API_VERSION]: API_VERSION },
      top,
      query: { assignedDate },
    });
    return apiRequest(options, toastMessageConfig);
  },

  /**
   * Retrieves a specific order by its ID.
   *
   * @async
   * @function getById
   * @param {Object} [params={}] - The parameters object
   * @param {string} params.orderId - The ID of the order to retrieve
   * @returns {Promise<any>} The order details response
   * @throws {Error} When the API request fails or order is not found
   *
   * @example
   * const orderDetails = await order.getById({ orderId: "order-123" });
   */
  getById: async ({ orderId } = {}) => {
    const options = await apiOptions({
      url: `${API_ORDER}/${orderId}`,
      method: "get",
      headers: { [X_API_VERSION]: API_VERSION },
    });
    return apiRequest(options);
  },

  /**
   * Retrieves order template summary data with optional filtering and pagination.
   *
   * @async
   * @function getOrderTemplateSummary
   * @param {Object} [params={}] - The query parameters object
   * @param {Object} [params.filters] - OData-style filters for the query
   * @param {Object} [params.sort] - Sorting configuration
   * @param {Object} [params.params] - Additional query parameters
   * @param {AbortSignal} [params.signal] - AbortController signal for request cancellation
   * @param {number} [params.top] - Maximum number of records to return
   * @param {number} [params.skip] - Number of records to skip
   * @param {Object} [params.toastMessageConfig] - Toast message configuration for UI feedback
   * @returns {Promise<any>} The order templates summary response
   * @throws {Error} When the API request fails
   *
   * @example
   * const templateSummary = await order.getOrderTemplateSummary({
   *   filters: { active: true },
   *   top: 25
   * });
   */
  getOrderTemplateSummary: async ({
    filters,
    sort,
    params,
    signal,
    top = MAX_PAGE_SIZE,
    skip,
    toastMessageConfig,
  } = {}) => {
    const options = await apiOptions({
      url: `${API_ORDER}-order-templates-summary`,
      method: "get",
      headers: { [X_API_VERSION]: API_VERSION },
      signal,
      params,
      filters,
      sort,
      top,
      skip,
    });
    return apiRequest(options, toastMessageConfig);
  },

  /**
   * Retrieves detailed information for all orders with optional filtering and pagination.
   *
   * @async
   * @function getDetailedAll
   * @param {Object} [params={}] - The query parameters object
   * @param {Object} [params.filters] - OData-style filters for the query
   * @param {Object} [params.sort] - Sorting configuration
   * @param {Object} [params.params] - Additional query parameters
   * @param {AbortSignal} [params.signal] - AbortController signal for request cancellation
   * @param {number} [params.top] - Maximum number of records to return
   * @param {number} [params.skip] - Number of records to skip
   * @param {Object} [params.toastMessageConfig] - Toast message configuration for UI feedback
   * @returns {Promise<any>} The detailed orders response with extended information
   * @throws {Error} When the API request fails
   *
   * @example
   * const detailedOrders = await order.getDetailedAll({
   *   filters: { customerId: "customer-456" },
   *   sort: { field: "priority", direction: "desc" }
   * });
   */
  getDetailedAll: async ({
    filters,
    sort,
    params,
    headers = {},
    signal,
    top,
    skip,
    toastMessageConfig,
  } = {}) => {
    const options = await apiOptions({
      url: `${API_ORDER}-detailed`,
      method: "get",
      headers: { ...headers, [X_API_VERSION]: API_VERSION },
      signal,
      params,
      filters,
      sort,
      top,
      skip,
    });
    return apiRequest(options, toastMessageConfig);
  },

  /**
   * Approves an order with the provided approval data.
   *
   * @async
   * @function approve
   * @param {Object} params - The parameters object
   * @param {string} params.orderId - The ID of the order to approve
   * @param {Object} params.data - The approval data (e.g., approver details, comments)
   * @param {Object} [params.toastMessageConfig] - Toast message configuration for UI feedback
   * @returns {Promise<any>} The approval response
   * @throws {Error} When the API request fails or approval is not authorized
   *
   * @example
   * const approvalResult = await order.approve({
   *   orderId: "order-123",
   *   data: {
   *     approverId: "user-456",
   *     comments: "Approved for immediate processing"
   *   }
   * });
   */
  approve: async ({ orderId, data, toastMessageConfig }) => {
    const options = await apiOptions({
      url: `${API_ORDER}/${orderId}/approve`,
      headers: { [X_API_VERSION]: API_VERSION },
      method: "put",
      data,
    });
    return apiRequest(options, toastMessageConfig);
  },

  getApproveOptions: async ({ orderId, data }) => {
    return apiOptions({
      url: `${API_ORDER}/${orderId}/approve`,
      headers: { [X_API_VERSION]: API_VERSION },
      method: "put",
      data,
    });
  },

  /**
   * Confirms an order, typically moving it to the next stage in the workflow.
   *
   * @async
   * @function confirm
   * @param {Object} params - The parameters object
   * @param {string} params.orderId - The ID of the order to confirm
   * @param {string} [params.modifiedOn] - Timestamp for optimistic concurrency control
   * @param {Object} params.data - Confirmation payload
   * @param {Object} [params.toastMessageConfig] - Toast message configuration for UI feedback
   * @returns {Promise<any>} The confirmation response
   * @throws {Error} When the API request fails or order cannot be confirmed
   *
   * @example
   * const confirmResult = await order.confirm({
   *   orderId: "order-123",
   *   modifiedOn: "2024-01-15T10:30:00Z"
   * });
   */
  confirm: async ({ orderId, modifiedOn, data, toastMessageConfig }) => {
    const options = await apiOptions({
      url: `${API_ORDER}/${orderId}/confirm`,
      headers: { [X_API_VERSION]: API_VERSION },
      method: "put",
      data,
      modifiedOn,
    });
    return apiRequest(options, toastMessageConfig);
  },

  getUndoConfirmOptions: async ({ orderId, data, modifiedOn }) =>
    apiOptions({
      url: `${API_ORDER}/${orderId}/confirm/undo`,
      headers: { [X_API_VERSION]: API_VERSION },
      method: "put",
      data,
      modifiedOn,
    }),

  getUndoApproveOptions: async ({ orderId, data, modifiedOn }) =>
    apiOptions({
      url: `${API_ORDER}/${orderId}/approve/undo`,
      headers: { [X_API_VERSION]: API_VERSION },
      method: "put",
      data,
      modifiedOn,
    }),

  getUndoAssignOptions: async ({ orderId, data, modifiedOn }) =>
    apiOptions({
      url: `${API_ORDER}/${orderId}/assign/undo`,
      headers: { [X_API_VERSION]: API_VERSION },
      method: "put",
      data,
      modifiedOn,
    }),

  getUndoCompletionOptions: async ({ orderId, data, modifiedOn }) =>
    apiOptions({
      url: `${API_ORDER}/${orderId}/force-complete/undo`,
      headers: { [X_API_VERSION]: API_VERSION },
      method: "put",
      data,
      modifiedOn,
    }),

  getUndoCancelOptions: async ({ orderId, data, modifiedOn }) =>
    apiOptions({
      url: `${API_ORDER}/${orderId}/cancel/undo`,
      headers: { [X_API_VERSION]: API_VERSION },
      method: "put",
      data,
      modifiedOn,
    }),

  /**
   * Assigns an order to a user or resource.
   *
   * @async
   * @function assign
   * @param {Object} params - The parameters object
   * @param {string} params.orderId - The ID of the order to assign
   * @param {string} [params.modifiedOn] - Timestamp for optimistic concurrency control
   * @param {Object} [params.toastMessageConfig] - Toast message configuration for UI feedback
   * @returns {Promise<any>} The assign response
   * @throws {Error} When the API request fails or order cannot be assigned
   *
   * @example
   * const assignResult = await order.assign({
   *   orderId: "order-123",
   *   modifiedOn: "2024-01-15T10:30:00Z"
   * });
   */
  assign: async ({ orderId, modifiedOn, toastMessageConfig }) => {
    const options = await apiOptions({
      url: `${API_ORDER}/${orderId}/assign`,
      headers: { [X_API_VERSION]: API_VERSION },
      method: "put",
      modifiedOn,
    });
    return apiRequest(options, toastMessageConfig);
  },

  /**
   * Places an order, typically making it active for processing.
   *
   * @async
   * @function place
   * @param {Object} params - The parameters object
   * @param {string} params.orderId - The ID of the order to place
   * @param {string} [params.modifiedOn] - Timestamp for optimistic concurrency control
   * @param {Object} [params.toastMessageConfig] - Toast message configuration for UI feedback
   * @returns {Promise<any>} The place order response
   * @throws {Error} When the API request fails or order cannot be placed
   *
   * @example
   * const placeResult = await order.place({
   *   orderId: "order-123",
   *   modifiedOn: "2024-01-15T10:30:00Z"
   * });
   */
  place: async ({ orderId, modifiedOn, toastMessageConfig }) => {
    const options = await apiOptions({
      url: `${API_ORDER}/${orderId}/place`,
      headers: { [X_API_VERSION]: API_VERSION },
      method: "put",
      modifiedOn,
    });
    return apiRequest(options, toastMessageConfig);
  },

  /**
   * Gets the API options for rescheduling an order (without executing the request).
   *
   * @async
   * @function getRescheduleOptions
   * @param {Object} params - The parameters object
   * @param {string} params.orderId - The ID of the order to reschedule
   * @param {Object} params.data - The new scheduling data
   * @returns {Promise<any>} The API options object for the reschedule request
   */
  getRescheduleOptions: async ({ orderId, data }) =>
    apiOptions({
      url: `${API_ORDER}/${orderId}/reschedule`,
      headers: { [X_API_VERSION]: API_VERSION },
      method: "put",
      data,
    }),

  /**
   * Reschedules an order with new scheduling information.
   *
   * @async
   * @function reschedule
   * @param {Object} params - The parameters object
   * @param {string} params.orderId - The ID of the order to reschedule
   * @param {Object} params.data - The new scheduling data (e.g., new dates, resources)
   * @param {Object} [params.toastMessageConfig] - Toast message configuration for UI feedback
   * @returns {Promise<any>} The reschedule response
   * @throws {Error} When the API request fails or rescheduling is not possible
   *
   * @example
   * const rescheduleResult = await order.reschedule({
   *   orderId: "order-123",
   *   data: {
   *     newStartDate: "2024-02-01T09:00:00Z",
   *     newEndDate: "2024-02-01T17:00:00Z",
   *     reason: "Resource availability change"
   *   }
   * });
   */
  reschedule: async ({ orderId, data, toastMessageConfig }) => {
    const options = await apiOptions({
      url: `${API_ORDER}/${orderId}/reschedule`,
      headers: { [X_API_VERSION]: API_VERSION },
      method: "put",
      data,
    });
    return apiRequest(options, toastMessageConfig);
  },

  /**
   * Gets the API options for placing an order on hold (without executing the request).
   * This method is useful for preparing request options that can be used later.
   *
   * @async
   * @function getHoldOptions
   * @param {Object} params - The parameters object
   * @param {string} params.orderId - The ID of the order to put on hold
   * @param {Object} params.data - The hold data (e.g., reason, duration)
   * @returns {Promise<any>} The API options object for the hold request
   *
   * @example
   * const holdOptions = await order.getHoldOptions({
   *   orderId: "order-123",
   *   data: { reason: "Equipment maintenance", expectedDuration: "2 hours" }
   * });
   */
  getHoldOptions: async ({ orderId, data }) =>
    apiOptions({
      url: `${API_ORDER}/${orderId}/hold`,
      headers: { [X_API_VERSION]: API_VERSION },
      method: "put",
      data,
    }),

  /**
   * Places an order on hold with the specified hold data.
   *
   * @async
   * @function hold
   * @param {Object} params - The parameters object
   * @param {string} params.orderId - The ID of the order to put on hold
   * @param {Object} params.data - The hold data (e.g., reason, expected duration)
   * @param {Object} [params.toastMessageConfig] - Toast message configuration for UI feedback
   * @returns {Promise<any>} The hold response
   * @throws {Error} When the API request fails or order cannot be put on hold
   *
   * @example
   * const holdResult = await order.hold({
   *   orderId: "order-123",
   *   data: {
   *     reason: "Waiting for customer approval",
   *     holdType: "customer_approval",
   *     notes: "Additional specifications needed"
   *   }
   * });
   */
  hold: async ({ orderId, data, toastMessageConfig }) => {
    const options = await apiOptions({
      url: `${API_ORDER}/${orderId}/hold`,
      headers: { [X_API_VERSION]: API_VERSION },
      method: "put",
      data,
    });
    return apiRequest(options, toastMessageConfig);
  },

  /**
   * Releases an order from hold status, allowing it to continue processing.
   *
   * @async
   * @function release
   * @param {Object} params - The parameters object
   * @param {string} params.orderId - The ID of the order to release from hold
   * @param {Object} params.data - The release data (e.g., release reason, next actions)
   * @param {string} [params.modifiedOn] - Timestamp for optimistic concurrency control
   * @param {Object} [params.toastMessageConfig] - Toast message configuration for UI feedback
   * @returns {Promise<any>} The release response
   * @throws {Error} When the API request fails or order cannot be released
   *
   * @example
   * const releaseResult = await order.release({
   *   orderId: "order-123",
   *   data: {
   *     releaseReason: "Customer approval received",
   *     notes: "Proceed with original schedule"
   *   },
   *   modifiedOn: "2024-01-15T10:30:00Z"
   * });
   */
  release: async ({ orderId, data, modifiedOn, toastMessageConfig }) => {
    const options = await apiOptions({
      url: `${API_ORDER}/${orderId}/release`,
      headers: { [X_API_VERSION]: API_VERSION },
      method: "put",
      data,
      modifiedOn,
    });
    return apiRequest(options, toastMessageConfig);
  },

  /**
   * Gets the API options for force completing accounting for an order (without executing the request).
   *
   * @async
   * @function getForceCompleteAccountingOptions
   * @param {Object} params - The parameters object
   * @param {string} params.orderId - The ID of the order to force complete accounting
   * @param {Object} [params.params] - Additional query parameters
   * @param {string} [params.modifiedOn] - Timestamp for optimistic concurrency control
   * @returns {Promise<any>} The API options object for the force complete accounting request
   *
   * @example
   * const options = await order.getForceCompleteAccountingOptions({
   *   orderId: "order-123",
   *   params: { skipValidation: true }
   * });
   */
  getForceCompleteAccountingOptions: async ({ orderId, params, modifiedOn }) =>
    apiOptions({
      url: `${API_ORDER}/${orderId}/force-complete-accounting`,
      headers: { [X_API_VERSION]: API_VERSION },
      method: "put",
      params,
      modifiedOn,
    }),

  /**
   * Gets the API options for force completing an order (without executing the request).
   *
   * @async
   * @function getForceCompleteOptions
   * @param {Object} params - The parameters object
   * @param {string} params.orderId - The ID of the order to force complete
   * @param {Object} [params.params] - Additional query parameters
   * @param {string} [params.modifiedOn] - Timestamp for optimistic concurrency control
   * @returns {Promise<any>} The API options object for the force complete request
   *
   * @example
   * const options = await order.getForceCompleteOptions({
   *   orderId: "order-123",
   *   params: { overrideValidation: true }
   * });
   */
  getForceCompleteOptions: async ({ orderId, params, modifiedOn }) =>
    apiOptions({
      url: `${API_ORDER}/${orderId}/force-complete`,
      headers: { [X_API_VERSION]: API_VERSION },
      method: "put",
      params,
      modifiedOn,
    }),

  /**
   * Gets the API options for cancelling an order (without executing the request).
   *
   * @async
   * @function getCancelOptions
   * @param {Object} params - The parameters object
   * @param {string} params.orderId - The ID of the order to cancel
   * @param {Object} params.data - The cancellation data
   * @param {string} [params.modifiedOn] - Timestamp for optimistic concurrency control
   * @returns {Promise<any>} The API options object for the cancel request
   */
  getCancelOptions: async ({ orderId, data, modifiedOn }) =>
    apiOptions({
      url: `${API_ORDER}/${orderId}/cancel`,
      data,
      headers: { [X_API_VERSION]: API_VERSION },
      method: "put",
      modifiedOn,
    }),

  /**
   * Cancels an order with the provided cancellation data.
   *
   * @async
   * @function cancel
   * @param {Object} params - The parameters object
   * @param {string} params.orderId - The ID of the order to cancel
   * @param {Object} params.data - The cancellation data (e.g., reason, cancellation type)
   * @param {string} [params.modifiedOn] - Timestamp for optimistic concurrency control
   * @param {Object} [params.toastMessageConfig] - Toast message configuration for UI feedback
   * @returns {Promise<any>} The cancellation response
   * @throws {Error} When the API request fails or order cannot be cancelled
   *
   * @example
   * const cancelResult = await order.cancel({
   *   orderId: "order-123",
   *   data: {
   *     reason: "Customer request",
   *     cancellationType: "full_refund",
   *     notes: "Customer changed requirements"
   *   },
   *   modifiedOn: "2024-01-15T10:30:00Z"
   * });
   */
  cancel: async ({ orderId, data, modifiedOn, toastMessageConfig }) => {
    const options = await apiOptions({
      url: `${API_ORDER}/${orderId}/cancel`,
      data,
      headers: { [X_API_VERSION]: API_VERSION },
      method: "put",
      modifiedOn,
    });
    return apiRequest(options, toastMessageConfig);
  },

  /**
   * Gets the API options for pushing an order by 24 hours (without executing the request).
   *
   * @async
   * @function getPushOptions
   * @param {Object} params - The parameters object
   * @param {string} params.orderId - The ID of the order to push
   * @param {string} [params.modifiedOn] - Timestamp for optimistic concurrency control
   * @returns {Promise<any>} The API options object for the push request
   */
  getPushOptions: async ({ orderId, modifiedOn }) =>
    apiOptions({
      url: `${API_ORDER}/${orderId}/push`,
      headers: { [X_API_VERSION]: API_VERSION },
      method: "put",
      modifiedOn,
    }),

  /**
   * Pushes an order by 24 hours.
   *
   * @async
   * @function push
   * @param {Object} params - The parameters object
   * @param {string} params.orderId - The ID of the order to push
   * @param {string} [params.modifiedOn] - Timestamp for optimistic concurrency control
   * @param {Object} [params.toastMessageConfig] - Toast message configuration for UI feedback
   * @returns {Promise<any>} The push response
   * @throws {Error} When the API request fails or order cannot be pushed
   *
   * @example
   * const pushResult = await order.push({
   *   orderId: "order-123",
   *   modifiedOn: "2024-01-15T10:30:00Z"
   * });
   */
  push: async ({ orderId, modifiedOn, toastMessageConfig }) => {
    const options = await apiOptions({
      url: `${API_ORDER}/${orderId}/push`,
      headers: { [X_API_VERSION]: API_VERSION },
      method: "put",
      modifiedOn,
    });
    return apiRequest(options, toastMessageConfig);
  },

  /**
   * Retrieves order counts grouped by status with optional filtering.
   *
   * @async
   * @function getCounts
   * @param {Object} [params={}] - The query parameters object
   * @param {Object} [params.filters] - OData-style filters for the query
   * @param {Object} [params.sort] - Sorting configuration
   * @param {Object} [params.params] - Additional query parameters
   * @param {AbortSignal} [params.signal] - AbortController signal for request cancellation
   * @param {number} [params.top] - Maximum number of records to return
   * @param {number} [params.skip] - Number of records to skip
   * @param {Object} [params.toastMessageConfig] - Toast message configuration for UI feedback
   * @returns {Promise<any>} The order counts by status response
   * @throws {Error} When the API request fails
   *
   * @example
   * const statusCounts = await order.getCounts({
   *   filters: { region: "north" },
   *   params: { groupBy: "status" }
   * });
   * // Returns: { pending: 25, confirmed: 15, completed: 60, cancelled: 5 }
   */
  getCounts: async ({
    filters,
    sort,
    params,
    signal,
    top = MAX_PAGE_SIZE,
    skip,
    toastMessageConfig,
  } = {}) => {
    const options = await apiOptions({
      url: `${API_ORDER}-counts-by-status`,
      method: "get",
      headers: { [X_API_VERSION]: API_VERSION },
      signal,
      params,
      filters,
      sort,
      top,
      skip,
    });
    return apiRequest(options, toastMessageConfig);
  },

  /**
   * Creates pre-bill invoices for an order.
   *
   * @async
   * @function preBillInvoices
   * @param {Object} params - The parameters object
   * @param {Object} params.data - The pre-billing data
   * @param {string} params.orderId - The ID of the order to pre-bill
   * @param {string} [params.modifiedOn] - Timestamp for optimistic concurrency control
   * @param {Object} [params.toastMessageConfig] - Toast message configuration for UI feedback
   * @returns {Promise<any>} The pre-bill invoices response
   * @throws {Error} When the API request fails or pre-billing is not possible
   *
   * @example
   * const preBillResult = await order.preBillInvoices({
   *   orderId: "order-123",
   *   data: {
   *     items: [...],
   *     billingAddress: {...},
   *     taxInfo: {...}
   *   },
   *   modifiedOn: "2024-01-15T10:30:00Z"
   * });
   */
  preBillInvoices: async ({
    data,
    orderId,
    modifiedOn,
    toastMessageConfig,
  }) => {
    const options = await apiOptions({
      url: `${API_ORDER}/${orderId}/prebill`,
      method: "put",
      modifiedOn,
      data,
      headers: { [X_API_VERSION]: API_VERSION },
    });
    return apiRequest(options, toastMessageConfig);
  },

  /**
   * Gets the API options for pre-billing invoices (without executing the request).
   *
   * @async
   * @function preBillInvoicesOptions
   * @param {Object} params - The parameters object
   * @param {Object} params.data - The pre-billing data
   * @param {string} params.orderId - The ID of the order to pre-bill
   * @param {string} [params.modifiedOn] - Timestamp for optimistic concurrency control
   * @returns {Promise<any>} The API options object for the pre-bill request
   *
   * @example
   * const preBillOptions = await order.preBillInvoicesOptions({
   *   orderId: "order-123",
   *   data: { items: [...] },
   *   modifiedOn: "2024-01-15T10:30:00Z"
   * });
   */
  preBillInvoicesOptions: async ({ data, orderId, modifiedOn }) => {
    return apiOptions({
      url: `${API_ORDER}/${orderId}/prebill`,
      method: "put",
      modifiedOn,
      data,
      headers: { [X_API_VERSION]: API_VERSION },
    });
  },

  /**
   * Gets the API options for finalizing invoices of an order (without executing the request).
   *
   * @async
   * @function getFinalizeInvoiceOfOrderOptions
   * @param {Object} params - The parameters object
   * @param {string} params.orderId - The ID of the order to finalize invoices
   * @param {Object} params.data - The finalization data
   * @returns {Promise<any>} The API options object for the finalize invoice request
   *
   * @example
   * const finalizeOptions = await order.getFinalizeInvoiceOfOrderOptions({
   *   orderId: "order-123",
   *   data: { finalizeAll: true }
   * });
   */
  getFinalizeInvoiceOfOrderOptions: async ({ orderId, data }) =>
    apiOptions({
      url: `${API_ORDER}/${orderId}/finalize-invoices`,
      headers: { [X_API_VERSION]: API_VERSION },
      method: "put",
      data,
    }),

  /**
   * Finalizes invoices for an order, making them ready for payment processing.
   *
   * @async
   * @function finalizeInvoiceOfOrder
   * @param {Object} params - The parameters object
   * @param {string} params.orderId - The ID of the order to finalize invoices
   * @param {Object} params.data - The finalization data
   * @param {Object} [params.toastMessageConfig] - Toast message configuration for UI feedback
   * @returns {Promise<any>} The finalize invoices response
   * @throws {Error} When the API request fails or invoices cannot be finalized
   *
   * @example
   * const finalizeResult = await order.finalizeInvoiceOfOrder({
   *   orderId: "order-123",
   *   data: {
   *     invoiceIds: ["inv-1", "inv-2"],
   *     finalizeDate: "2024-01-15T10:30:00Z"
   *   }
   * });
   */
  finalizeInvoiceOfOrder: async ({ orderId, data, toastMessageConfig }) => {
    const options = await apiOptions({
      url: `${API_ORDER}/${orderId}/finalize-invoices`,
      headers: { [X_API_VERSION]: API_VERSION },
      method: "put",
      data,
    });
    return apiRequest(options, toastMessageConfig);
  },

  /**
   * Gets the API options for voiding an order (without executing the request).
   *
   * @async
   * @function getVoidOrderOptions
   * @param {Object} params - The parameters object
   * @param {string} params.orderId - The ID of the order to void
   * @param {string} [params.modifiedOn] - Timestamp for optimistic concurrency control
   * @returns {Promise<any>} The API options object for the void order request
   *
   * @example
   * const voidOptions = await order.getVoidOrderOptions({
   *   orderId: "order-123",
   *   modifiedOn: "2024-01-15T10:30:00Z"
   * });
   */
  getVoidOrderOptions: async ({ orderId, modifiedOn }) =>
    apiOptions({
      url: `${API_ORDER}/${orderId}/void`,
      headers: { [X_API_VERSION]: API_VERSION },
      method: "put",
      modifiedOn,
    }),

  getEmailHistory: async ({ orderId, toastMessageConfig }) => {
    const options = await apiOptions({
      url: `${API_ORDER}/${orderId}/emails`,
      method: "get",
      headers: { [X_API_VERSION]: API_VERSION },
    });

    return apiRequest(options, toastMessageConfig);
  },

  getEmailTemplateContacts: async ({
    orderId,
    emailType,
    toastMessageConfig,
  }) => {
    const options = await apiOptions({
      url: `${API_ORDER}/${orderId}/emailtypes/${emailType}/contacts`,
      method: "get",
      headers: { [X_API_VERSION]: API_VERSION },
    });

    return apiRequest(options, toastMessageConfig);
  },

  sendEmail: async ({ orderId, data, toastMessageConfig }) => {
    const options = await apiOptions({
      url: `${API_ORDER}/${orderId}/send-email`,
      method: "post",
      headers: { [X_API_VERSION]: API_VERSION },
      data,
    });

    return apiRequest(options, toastMessageConfig);
  },
};
